import React, { useState } from 'react';
import { Check, Lock, Loader2, Building2, Plus, ArrowRight, X } from 'lucide-react';
import axios from 'axios';

import API from './apiConfig';

/**
 * Paywall Page / Modal — shown after registration/login (or when user
 * tries to create a 2nd project / upload further plans).
 *
 * Props:
 *   - onTrialAccept(): user clicked "Jetzt kostenlos testen"
 *   - onClose():       optional close (X) — only shown when used as modal
 *   - title?, subtitle?: customise headline (e.g. "Limit erreicht")
 *   - hideTrialOption?: hide the kostenlos-testen-link (e.g. when trial already used)
 */
export default function Paywall({ onTrialAccept, onClose, title, subtitle, hideTrialOption }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pricing, setPricing] = useState({ owner_price: 269.99, member_price: 149.99 });

  React.useEffect(() => {
    axios.get(`${API}/api/pricing`).then(r => setPricing(r.data)).catch(() => {});
  }, []);

  const fmt = (n) => n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const startCheckout = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/api/stripe/create-checkout`, {
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        setError('Checkout-URL konnte nicht erstellt werden.');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Fehler beim Starten der Bezahlung.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center px-4 py-10 relative">
      {onClose && (
        <button
          onClick={onClose}
          data-testid="paywall-close"
          className="absolute top-6 right-6 p-2 text-zinc-500 hover:text-zinc-200 transition-colors"
          aria-label="Schließen"
        >
          <X className="w-5 h-5" />
        </button>
      )}

      <div className="w-full max-w-4xl">
        {/* Header */}
        <div className="text-center mb-10">
          <img src="/logo.png" alt="LAIzyBlueprint" className="h-14 mx-auto mb-4" />
          <h1
            className="text-3xl sm:text-4xl font-bold mb-3 leading-tight"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            data-testid="paywall-title"
          >
            {title || 'Volle Engine. Jetzt freischalten.'}
          </h1>
          <p className="text-zinc-400 text-base max-w-xl mx-auto leading-relaxed">
            {subtitle ||
              'Wählen Sie Ihre Firmen-Lizenz, um unbegrenzt Pläne zu analysieren und Leistungsverzeichnisse zu generieren.'}
          </p>
        </div>

        {/* Pricing cards */}
        <div className="grid md:grid-cols-2 gap-5 mb-8 max-w-3xl mx-auto">
          {/* Firmen-Lizenz */}
          <div className="relative p-7 bg-zinc-900 border-2 border-blue-500/50 rounded-2xl shadow-lg shadow-blue-500/10">
            <div className="absolute -top-3 left-7 px-2.5 py-0.5 bg-blue-500 text-white font-mono text-[10px] rounded">
              Empfohlen
            </div>
            <div className="flex items-center gap-2.5 mb-2 mt-1.5">
              <div className="w-9 h-9 bg-blue-500/15 rounded-lg flex items-center justify-center">
                <Building2 className="w-4 h-4 text-blue-400" />
              </div>
              <h3 className="font-bold text-white text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Firmen-Lizenz
              </h3>
            </div>
            <p className="text-zinc-400 text-sm mb-5">Voller Engine-Zugang für ein Unternehmen.</p>
            <div className="flex items-baseline gap-1.5 mb-1">
              <span className="text-4xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {fmt(pricing.owner_price)} €
              </span>
              <span className="text-zinc-500 text-sm">netto / Monat</span>
            </div>
            <p className="text-zinc-600 text-xs mb-6">pro Firma · monatlich kündbar</p>
            <ul className="space-y-2 mb-6 text-sm">
              {[
                'Unbegrenzte Grundriss-Analysen',
                'Vollständiges LV-PDF-Export',
                'Schnitt-Analyse, Editor, alle Werkzeuge',
                '1 Hauptnutzer inklusive',
                'E-Mail-Support',
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-zinc-200">
                  <Check className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={startCheckout}
              disabled={loading}
              data-testid="paywall-pay-btn"
              className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 text-white font-medium rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Jetzt bezahlen <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>

          {/* Add-on info */}
          <div className="relative p-7 bg-zinc-900/60 border border-zinc-800 rounded-2xl">
            <div className="absolute -top-3 left-7 px-2.5 py-0.5 bg-zinc-800 border border-zinc-700 text-zinc-300 font-mono text-[10px] rounded">
              Add-on
            </div>
            <div className="flex items-center gap-2.5 mb-2 mt-1.5">
              <div className="w-9 h-9 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                <Plus className="w-4 h-4 text-emerald-400" />
              </div>
              <h3 className="font-bold text-white text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Pro weiterem Mitarbeiter
              </h3>
            </div>
            <p className="text-zinc-400 text-sm mb-5">Für jeden zusätzlichen Mitarbeiter, der die Engine nutzt.</p>
            <div className="flex items-baseline gap-1.5 mb-1">
              <span className="text-4xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {fmt(pricing.member_price)} €
              </span>
              <span className="text-zinc-500 text-sm">netto / Monat</span>
            </div>
            <p className="text-zinc-600 text-xs mb-6">pro zusätzlichem Nutzer · jederzeit erweiterbar</p>
            <ul className="space-y-2 text-sm">
              {[
                'Eigener Account in der Firmen-Lizenz',
                'Alle Engine-Features ohne Einschränkung',
                'Gemeinsame Projektverwaltung im Team',
                'Über Firmen-Lizenz hinzubuchbar',
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-zinc-200">
                  <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {error && (
          <div className="max-w-3xl mx-auto mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm text-center">
            {error}
          </div>
        )}

        {/* Trial option (klein darunter) */}
        {!hideTrialOption && onTrialAccept && (
          <div className="text-center max-w-md mx-auto">
            <button
              onClick={onTrialAccept}
              data-testid="paywall-trial-btn"
              className="text-zinc-400 hover:text-white text-sm underline underline-offset-4 transition-colors"
            >
              Jetzt kostenlos testen
            </button>
            <p className="text-zinc-600 text-xs mt-2 leading-relaxed">
              1 Projekt mit 1 Grundriss · LV-Export inklusive · keine Kreditkarte nötig
            </p>
          </div>
        )}

        <p className="text-center text-zinc-700 text-[10px] mt-10 max-w-md mx-auto">
          Alle Preise zzgl. gesetzlicher MwSt. Sichere Zahlung über Stripe.
        </p>
      </div>
    </div>
  );
}

/**
 * Inline lock-icon button — used for "Neues Projekt" when free trial is consumed
 * or for additional grundriss-uploads.
 */
export function LockButton({ onClick, label, testId, className }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId || 'lock-button'}
      className={
        className ||
        'flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-md text-sm font-mono font-medium transition-colors text-zinc-300'
      }
      title="Premium-Funktion — jetzt freischalten"
    >
      <Lock className="w-3.5 h-3.5 text-amber-400" /> {label || 'Neues Projekt'}
    </button>
  );
}
